import React, { useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { RootState } from '../../redux/store'
import { LoginStatus } from '../../redux/slices/userSlice'
import { Smartico } from '../../core/Smartico'
import { Link, useLocation } from 'react-router-dom'
import Utils from '../../core/Utils'
import { useOutsideClick } from '../../hooks/useOutsideClick'
import { closeMenu } from '../../redux/slices/burgerMenuSlice'
import FreeSpinIcon from '../../img/svgComponents/FreeSpinIcon'
import { useTranslation } from '../../hooks/useTranslation'
import { ModalType } from '../../core/enums/Modals'
import Terms from '../../Terms'
import { API } from '../../core/API'
import GamePageSidebar from './GamePageSidebar/GamePageSidebar'
import GamificationBlock from '../GamificationBlock/GamificationBlock'
import { gameWidgetUrlForPath, WIDGETS } from '../../core/widgets'
import {
	BallIcon,
	CasinoIcon,
	ChevronDownIcon,
	GamepadIcon,
	JackpotIcon,
	LeaderboardIcon,
	LevelsIcon,
	LotteryIcon,
	LoyaltyIcon,
	MissionIcon,
	PrizeDropIcon,
	QuizIcon,
	RaffleIcon,
	StoreIcon,
	SwordsIcon,
} from '../../img/svgComponents/UiIcons'

interface SidebarProps {
	isClosed?: boolean
}

const Sidebar = ({ isClosed }: SidebarProps) => {
	const isMobile = Utils.isMobile()
	const { loggedIn, user } = useAppSelector((state: RootState) => state.user)

	const sideBarRef = useRef(null)
	const dispatch = useAppDispatch()
	const { getTexts } = useTranslation(false)

	const [isCollapsed, setIsCollapsed] = useState(false)
	const [activeMenuItem, setActiveMenuItem] = useState<string | null>(null)
	const [selectedMenuItem, setSelectedMenuItem] = useState<string | null>(null)
	const [loyaltyExpanded, setLoyaltyExpanded] = useState(true)

	const location = useLocation()

	const isGamePage = location.pathname.includes('/game/')
	const isWidgetGame = !!gameWidgetUrlForPath(location.pathname)

	const loyaltySubItems = [
		{ label: 'Missions', url: '/missions', Icon: MissionIcon },
		{ label: 'Battles', url: '/tournament-list', Icon: SwordsIcon },
		{ label: 'Minigames', url: '/mini-games', Icon: GamepadIcon },
		{ label: 'Levels', url: '/vip', Icon: LevelsIcon },
		{ label: 'Marketplace', url: '/store', Icon: StoreIcon },
		{ label: 'Quiz', url: '/quiz', Icon: QuizIcon },
		{ label: 'Sports Prediction', url: '/matchx', Icon: BallIcon },
		// { label: 'Daily Drops', url: '' },
		{ label: 'Prize Drop', url: '/prize-drop', Icon: PrizeDropIcon },
		{ label: 'Jackpots', url: '/jackpots', Icon: JackpotIcon },
		{ label: 'Raffle', url: '/raffle', Icon: RaffleIcon },
		// { label: 'Doodles', url: '' },
	]

	useEffect(() => {
		if (location.pathname === '/') {
			setSelectedMenuItem('Casino')
		} else if (location.pathname === '/sports') {
			setSelectedMenuItem('Sports')
		} else {
			setSelectedMenuItem(null)
		}
	}, [location.pathname])

	const toggleSidebar = () => {
		setIsCollapsed(!isCollapsed)
	}

	const doOpenWidget = () => {
		if (loggedIn === LoginStatus.LoggedIn && user) {
			Smartico.dp('dp:gf')
		}
		dispatch(closeMenu())
	}

	const toggleMenuItem = (itemName: string) => {
		setActiveMenuItem(activeMenuItem === itemName ? null : itemName)
	}

	const handleMenuItemClick = (itemName: string) => {
		setSelectedMenuItem(itemName)
	}

	useOutsideClick(
		sideBarRef,
		() => {
			isMobile && dispatch(closeMenu())
		},
		ModalType.BottomMenu,
	)

	const submenuContainer = document.querySelector('.sidebar')

	if (submenuContainer) {
		submenuContainer.addEventListener('click', (event) => {
			const target = event.target as Element

			if (target && target.classList.contains('submenu-item')) {
				dispatch(closeMenu())
			}
		})
	}

	const doResetUser = async () => {
		if (loggedIn === LoginStatus.LoggedIn && user) {
			if (
				window.confirm(
					'You are going to reset gamification status and start from the beginning. All your collected points, completed missions, achieved level, tournaments will be reset to initial state. Do you want to proceed?',
				)
			) {
				;(async () => {
					// setShowLoading(true)
					if (loggedIn === LoginStatus.LoggedIn && Smartico.identified()) {
						Smartico.clear()
					}
					await API.duplicateUser()
					// showToast("New user profile is ready...");
					// setShowLoading(false);

					// let reloadTriggered = false
					window.location.reload()

					// Smartico.lib().on('user_registered', () => {
					// 	if (!reloadTriggered) {
					// 		reloadTriggered = true
					// 		window.location.reload()
					// 	}
					// })
				})()
			}
		}
		dispatch(closeMenu())
	}

	return (
		<>
			{!isGamePage ? (
				<div
					ref={sideBarRef}
					className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isClosed && isMobile && !isGamePage ? 'closed' : ''}`}
					style={isGamePage ? { width: '516px', height: '100vh', padding: 0, overflow: 'hidden', gap: 0 } : {}}
				>
					{!isMobile && (
						<div className="top-section" style={isGamePage ? { padding: '36px 0' } : {}}>
							{!isGamePage && <div className={`btn collapse-btn ${isCollapsed ? 'collapsed' : ''}`} onClick={toggleSidebar}></div>}
							{!isCollapsed && <Link className="logo" to="/"></Link>}
						</div>
					)}

					<div className="sidebar-content-wrapper">
					<div
						className="free-spin"
						onClick={() => {
							doOpenWidget()
						}}
					>
						<FreeSpinIcon width={isCollapsed ? '24' : ''} height={isCollapsed ? '24' : ''} />

						<div className="free-spins-texts">
							<div className="title">{getTexts('loyalty_title')}</div>
							<div className="subtitle">{getTexts('loyalty_subtitle')}</div>
						</div>
						<div className="arrow-btn">
							<div className="arrow-btn-icon"></div>
						</div>
					</div>
					<Link className="collectibles-card" to="/my-collectibles" onClick={() => dispatch(closeMenu())}>
							<div className="collectibles-icon"></div>
							<div className="collectibles-texts">
								<div className="title">{getTexts('my_collectibles_title')}</div>
								<div className="subtitle">{getTexts('my_collectibles_subtitle')}</div>
							</div>
							<div className="collectibles-arrow"></div>
						</Link>
						<div className="deep-link-section">
						{[
							{ className: 'battles-btn', url: '/tournament-list', label: getTexts('tournaments'), Icon: SwordsIcon },
							{ className: 'missions-btn', url: '/missions', label: getTexts('missions'), Icon: MissionIcon },
							{ className: 'leaderboards-btn', url: '/leaderboards', label: getTexts('leaderboard'), Icon: LeaderboardIcon },
						].map(({ className, url, label, Icon }) => (
							<Link
								key={url}
								className={`deep-link ${className} ${location.pathname === url ? 'selected' : ''}`}
								to={url}
								title={label}
								onClick={() => dispatch(closeMenu())}
							>
								<Icon className="icon" size={24} /> <span>{label}</span>
							</Link>
						))}
					</div>
					{loggedIn === LoginStatus.LoggedIn && !isCollapsed && (
						<GamificationBlock
							domId={WIDGETS.minigames.domId}
							url={WIDGETS.minigames.url}
							fit="content"
							className="gamification-block--sidebar"
						/>
					)}
					</div>
					<div className="menu-section">
						<Link
							className={`menu-item casino ${selectedMenuItem === 'Casino' ? 'selected' : ''}`}
							onClick={() => {
								handleMenuItemClick('Casino')
								if (!isCollapsed) {
									toggleMenuItem('Casino')
								}
								dispatch(closeMenu())
							}}
							to="/"
							style={{ justifyContent: `${isCollapsed ? 'center' : 'spaceBetween'}` }}
						>
							<CasinoIcon className="icon" size={22} />
							<div className="text">{getTexts('casino')}</div>
						</Link>

						<div className={`loyalty-menu ${loyaltyExpanded ? 'expanded' : ''}`}>
							<button
								type="button"
								className="menu-item loyalty"
								onClick={() => {
									if (!isCollapsed) {
										setLoyaltyExpanded(!loyaltyExpanded)
									}
								}}
								style={{ justifyContent: `${isCollapsed ? 'center' : 'spaceBetween'}` }}
							>
								<LoyaltyIcon className="icon" size={22} />
								{!isCollapsed && (
									<>
										<div className="text">Loyalty</div>
										<ChevronDownIcon className={`collapse-icon ${loyaltyExpanded ? 'expanded' : ''}`} size={18} />
									</>
								)}
							</button>
							{loyaltyExpanded && !isCollapsed && (
							<div className="loyalty-submenu">
								{loyaltySubItems.map((item) => {
									const isActive = location.pathname === item.url

									return (
										<Link
											key={item.label}
											to={item.url}
											className={`loyalty-submenu-item ${
												isActive ? 'selected' : ''
											}`}
											onClick={() => dispatch(closeMenu())}
										>
											<item.Icon className="submenu-icon" size={18} />
											{item.label}
										</Link>
									)
								})}
							</div>
						)}
						</div>

						<Link
							className={`menu-item sports ${selectedMenuItem === 'Sports' ? 'selected' : ''}`}
							onClick={() => {
								handleMenuItemClick('Sports')
								if (!isCollapsed) {
									toggleMenuItem('Sports')
								}
								dispatch(closeMenu())
							}}
							to="/sports"
							style={{ justifyContent: `${isCollapsed ? 'center' : 'spaceBetween'}` }}
						>
							<BallIcon className="icon" size={22} />
							<div className="text">{getTexts('sports_betting')}</div>
						</Link>
						<div className={`menu-item lottery`} style={{ justifyContent: `${isCollapsed ? 'center' : 'spaceBetween'}`, cursor: 'default' }}>
							<LotteryIcon className="icon" size={22} />
							<div className="text">
								{getTexts('lottery')} <div className="soon">{getTexts('coming_soon')}</div>
							</div>
						</div>
					</div>
					<div className="sidebar-footer">
						{loggedIn === LoginStatus.LoggedIn && (
							<div className="reset-btn" onClick={doResetUser}>
								<div className="reset-icon" />
								<div className="text">
								{getTexts('reset_progress')}
								</div>
							</div>
						)}
						<Terms />
					</div>
				</div>
			) : !isMobile && !isWidgetGame ? (
				<GamePageSidebar />
			) : null}
		</>
	)
}

export default Sidebar
